import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import BottomNavigation from '@material-ui/core/BottomNavigation';
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import ChatIcon from '@material-ui/icons/Chat';

import TicketsManagerTabs from "../../components/TicketsManagerTabs/";
import Ticket from "../../components/Ticket/";
import TicketAdvancedLayout from "../../components/TicketAdvancedLayout";

import { TicketsContext } from "../../context/Tickets/TicketsContext";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
    header: {
        backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
        "& .MuiBottomNavigation-root": {
            backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
            borderBottom: theme.palette.type === "dark"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.08)",
            // Mobile: the segmented Tickets/Chat header used to claim ~80px
            // (default BottomNavigation = 56px + the parent 56px grid track
            // double-counted by the inner icon/label stack). On 360x640
            // that pushed the filter row + first ticket below the fold.
            // Compress to ~44px on xs so the actual list gets that
            // vertical real-estate back.
            [theme.breakpoints.down("xs")]: {
                height: 44,
                minHeight: 44,
            },
        },
        "& .MuiBottomNavigationAction-root": {
            color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "#64748b",
            transition: "all 0.2s ease",
            "&.Mui-selected": {
                color: theme.palette.type === "dark" ? "#2ee37a" : "#25d366",
            },
            [theme.breakpoints.down("xs")]: {
                minWidth: 0,
                padding: "2px 8px 2px",
                // Reset MUI's "selected gets extra top padding" rule that
                // bumps the selected pill 6px down — kills the vertical
                // alignment between Chat and Tickets when one is active.
                "&.Mui-selected": {
                    paddingTop: 2,
                },
            },
        },
        "& .MuiBottomNavigationAction-label": {
            fontSize: "13px",
            fontWeight: 600,
            "&.Mui-selected": {
                fontSize: "13px",
            },
            [theme.breakpoints.down("xs")]: {
                fontSize: "11px",
                "&.Mui-selected": {
                    fontSize: "11px",
                },
            },
        },
        // Shrink the stacked icon on xs — default is 24px which together
        // with the label and BottomNavigation's internal padding hit ~80px.
        "& .MuiBottomNavigationAction-root .MuiSvgIcon-root": {
            [theme.breakpoints.down("xs")]: {
                fontSize: 18,
            },
        },
    },
    content: {
        overflow: "auto",
        backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
        // Prevent flicking past the top/bottom edge of this panel from
        // rubber-banding/scroll-chaining into the outer page (2026-07 jitter
        // audit). WebkitOverflowScrolling is a legacy iOS momentum fallback.
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
    },
    placeholderContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    },
    placeholderItem: {
        color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "#64748b",
    }
}));

const TicketAdvanced = (props) => {
	const classes = useStyles();
	const { ticketId } = useParams();
	const [option, setOption] = useState(0);
    const { currentTicket, setCurrentTicket } = useContext(TicketsContext)

    useEffect(() => {
        if(currentTicket.id !== null) {
            setCurrentTicket({ id: currentTicket.id, code: '#open' })
        }
        if (!ticketId) {
            setOption(1)
        }
        return () => {
            setCurrentTicket({ id: null, code: null })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (currentTicket.id !== null) {
            setOption(0)
        }
    }, [currentTicket])

    // Mirror ticketId -> option for the OTHER direction. The first mount
    // useEffect above only runs once; if the URL drops the ticketId later
    // (Move-to action does history.push("/tickets")), nothing flipped
    // option back to 1, leaving the page on the chat tab with an empty
    // placeholder. This is what produced Raviv's mobile move-from-custom
    // bug (2026-05-14): after Move-to-Pending the chat panel stayed
    // visible with a 'select a ticket' placeholder, pushing the actual
    // tickets list down off-screen even though display:none collapses
    // the chat panel for the grid — the issue compounded because option
    // still pointed at the empty chat side. Tying option to ticketId
    // here keeps the two in sync regardless of which side initiated
    // the change.
    useEffect(() => {
        if (!ticketId) setOption(1);
    }, [ticketId])

	const renderPlaceholder = () => {
		return <Box className={classes.placeholderContainer}>
            <div className={classes.placeholderItem}>{i18n.t("chat.noTicketMessage")}</div><br />
            <Button onClick={() => setOption(1)} variant="contained" color="primary">
                {i18n.t("chat.selectTicket")}
            </Button>
        </Box>
	}

	return (
        <TicketAdvancedLayout>
            <Box className={classes.header}>
                <BottomNavigation
                    value={option}
                    onChange={(event, newValue) => {
                        setOption(newValue);
                    }}
                    showLabels
                    className={classes.root}
                >
                   <BottomNavigationAction label={i18n.t("chat.title")} icon={<ChatIcon />} />
                   <BottomNavigationAction label={i18n.t("mainDrawer.listItems.tickets")} icon={<QuestionAnswerIcon />} />
             </BottomNavigation>
            </Box>
            {/* Both panels stay mounted; CSS toggles which is visible.
                Reverted to display:none after 2026-05-14: the
                height:0 + visibility:hidden trick from `fef94be` was
                meant to keep the scroll container measurable for
                pagination, but TicketAdvancedLayout uses
                gridTemplateRows: "56px 1fr" — only two rows defined
                for three children. With height:0 the inactive Box
                still claimed its 1fr grid track, leaving a big empty
                whitespace gap between the header and the visible
                panel (Raviv's screenshot 2026-05-14, mobile move-to
                Pending). display:none collapses the grid row so the
                visible panel takes the full available height.
                Scroll pagination on the INNER system tabs (Open /
                Pending / Closed inside TicketsManagerTabs) still
                uses the height-toggle trick — that's a separate
                layer and continues to work. */}
            <Box
                className={classes.content}
                style={option === 0 ? undefined : { display: "none" }}
            >
                {ticketId ? <Ticket /> : renderPlaceholder()}
            </Box>
            <Box
                className={classes.content}
                style={option === 1 ? undefined : { display: "none" }}
            >
                {/* paused stops the offscreen tab's socket dispatch/refetch
                    churn while the user is on the Chat side (2026-07 jitter
                    audit) — see TicketsManagerTabs' own paused doc. */}
                <TicketsManagerTabs paused={option !== 1} />
            </Box>
        </TicketAdvancedLayout>
	);
};

export default TicketAdvanced;
